/**
 * The low-priority inbox: which tab is showing, and the sender rules behind it.
 *
 * # Two tabs, not two folders
 *
 * The whole feature is a local judgement about senders. Nothing is moved, no
 * label changes, and no flag is written upstream -- the daemon classifies into
 * `lowPriority` and this client asks for one bucket or the other. That is why
 * the control is a tab strip over the list rather than a row in the sidebar: a
 * sidebar entry beside Archive and Junk would claim a folder exists, someone
 * would go looking for it in Thunderbird, and it would not be there. A tab is
 * two views of the same mailbox, which is exactly what this is.
 *
 * # Why the bucket is not one of `lib/filters.ts`
 *
 * A filter is a temporary narrowing of a list, announced loudly, cleared with
 * one button, and counted in a badge that says how many are on. The bucket is
 * none of those: it is *where you are*, one of two places the inbox can be
 * looked at from, and it is never "off". Putting it in the filter bar would put
 * "1 filter active" on screen permanently and offer a Clear button that would
 * have to mean "show both tabs at once" -- a third state nothing else in the
 * design has.
 *
 * So it is scope-like, and it behaves like scope: changing it reloads the list
 * and clears the selection, because forty ticks were an answer to "these ones,
 * out of what I am looking at".
 *
 * # It survives leaving the inbox, and does not apply outside it
 *
 * Archive, Sent and a search have one bucket, not two -- the tabs are hidden
 * there and no condition is sent. Coming back to the inbox returns to the tab
 * you left, which is what Outlook does and what anybody who deliberately went
 * to look at their low-priority mail expects when they check a folder and come
 * back.
 */

// The transport seam. `@transport` resolves at build time to the browser
// implementation or the desktop one; nothing below can tell which. Imported
// under the name `fetch` so the call sites are untouched and this import is
// the whole of the difference. See `lib/net.types.ts`.
import { authHeaders } from "./settings";
import { request as fetch } from "@transport";
import { signal } from "@preact/signals";

import type { Email } from "./types";

export type Bucket = "focused" | "low";

/**
 * Which tab the inbox is showing. Not persisted across reloads, for the reason
 * `lib/filters.ts` gives about filters: a fresh tab days later, showing a
 * fraction of the inbox for a reason nobody remembers choosing, is the failure
 * mode to design out. It is far more visible than a filter -- the tab is
 * underlined -- but the same argument applies at less cost.
 */
export const bucket = signal<Bucket>("focused");

/**
 * Which tab a message belongs in, decided from the copy in hand.
 *
 * Mirrors the daemon's `Condition::low_priority`, and has to keep mirroring it:
 * a flagged message is never in the low-priority tab, whatever its
 * classification says. Used only to notice that something the reader *just
 * did* has moved a row out of the tab it is sitting in -- never to decide what
 * to show, which is the server's job over the whole result set rather than over
 * the forty rows in the buffer.
 */
export function bucketOf(email: Email): Bucket {
  if (!email.lowPriority) return "focused";
  return email.keywords["$flagged"] ? "focused" : "low";
}

// ---- the rules ---------------------------------------------------------------

/** What a rule compares the sender's address against. */
export type RuleScope = "address" | "domain";

export interface Rule {
  id: number;
  scope: RuleScope;
  /**
   * What is really matched, after normalisation. Worth showing beside
   * `display` when the two differ: somebody who blacklisted a Gmail address
   * written with dots deserves to see that the dots are not part of the
   * comparison, rather than concluding the rule is broken.
   */
  pattern: string;
  /** The spelling the rule was created from. The label. */
  display: string;
  createdAt: number;
}

/**
 * What a write did, including how much of the archive moved.
 *
 * The count is the point. Blacklisting a sender reaches backwards through every
 * message they have ever sent, and a reader who is told "moved 212 messages"
 * understands what the feature does in a way no amount of explanatory text
 * achieves. It is also the honest report when the answer is zero.
 */
export interface WriteResult {
  rule: Rule | null;
  reclassified: number;
}

export class PriorityError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "PriorityError";
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    // On the bodyless requests too: the daemon refuses a cookie-authenticated
    // write that is not `application/json`, and that refusal is what stops a
    // cross-site form forging one.
    // `authHeaders` rather than a bare content-type: it sends the same
    // content-type and adds the bearer when this build has one. A browser has
    // none and is authenticated by its cookie; a packaged build has no cookie,
    // and this line is the whole of what makes the request authenticated at
    // all. See `lib/settings.ts` for what leaving it out cost.
    headers: authHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const problem = await res.json();
      detail = problem.error ?? problem.detail ?? detail;
    } catch {
      // A proxy error page is HTML; the status line is all there is.
    }
    throw new PriorityError(detail, res.status);
  }
  return (await res.json()) as T;
}

export async function list(): Promise<Rule[]> {
  const page = await request<{ items: Rule[] }>("GET", "/priority-rules");
  return page.items;
}

export function create(pattern: string, scope: RuleScope = "address"): Promise<WriteResult> {
  return request<WriteResult>("POST", "/priority-rules", { pattern, scope });
}

export function setScope(id: number, scope: RuleScope): Promise<WriteResult> {
  return request<WriteResult>("PATCH", `/priority-rules/${id}`, { scope });
}

export function remove(id: number): Promise<WriteResult> {
  return request<WriteResult>("DELETE", `/priority-rules/${id}`);
}

/** The address a rule about this message would be about. */
export function senderOf(email: Email): string | undefined {
  return email.from?.[0]?.email ?? undefined;
}

/** The domain half of an address, for offering the wider rule. */
export function domainOf(address: string): string | undefined {
  const at = address.lastIndexOf("@");
  return at > 0 ? address.slice(at + 1) : undefined;
}
